import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Default styles
import NavigateBack from "../NavigateBack";
import { useParams, useNavigate } from "react-router-dom";
import { createCoupon, getCoupons, updateCoupon, generateCouponDescription, getOnlyProductNames, getCategories, isBranchMarketplaceEnabled } from "../../services/ClientService";
import { toast } from "react-toastify";
import { customSelect } from "../Styles/CustomStyles";
import { adminFetchJson } from "../../services/apiService";

// Selection mode options for product source
const selectionModeOptions = [
    { value: 'manual', label: 'Select Products Manually' },
    { value: 'category', label: 'Select by Category' },
    { value: 'categories', label: 'Select by Multiple Categories' },
    { value: 'subcategory', label: 'Select by Subcategory' },
    { value: 'subcategories', label: 'Select by Multiple Sub Categories' }
];

const CreateCoupon = () => {
    const { _id } = useParams();
    const navigate = useNavigate();
    const productsInitialized = useRef(false);
    const [couponData, setCouponData] = useState({
        name: "",
        couponCode: "",
        description: "",
        discountType: "percentage",
        discountValue: "",
        minOrderValue: "",
        maxDiscountValue: "",
        maxUsageTotal: "",
        startDate: new Date(),
        endDate: new Date(),
        products: [],
        storeType: "",
        oneTimeUsePerUser: false, // New: Limit coupon to one-time use per customer
        isHidden: false, // New: Hide coupon from customer list (can still be applied via code)
    });

    // Error state for all fields
    const [errors, setErrors] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const [selectionMode, setSelectionMode] = useState(selectionModeOptions[0]); // Default to manual
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [enabledStoreTypes, setEnabledStoreTypes] = useState({
        online: true,
        offline: true,
        marketplace: false
    });

    const discountTypeOptions = [
        { value: "percentage", label: "Percentage" },
        { value: "fixed", label: "Fixed Amount" },
    ];

    // Base store type options - will be filtered based on branch settings
    const allStoreTypeOptions = [
        { value: "online", label: "Online Store" },
        { value: "offline", label: "Offline Store" },
        { value: "marketplace", label: "Marketplace" }
    ];

    // Filter store type options based on enabled store types
    const storeTypeOptions = allStoreTypeOptions.filter(option => {
        if (option.value === "online") return enabledStoreTypes.online;
        if (option.value === "offline") return enabledStoreTypes.offline;
        if (option.value === "marketplace") return enabledStoreTypes.marketplace;
        return false;
    });

    // Fetch branch data to determine enabled store types
    useEffect(() => {
        const fetchBranchData = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem("userData"));
                if (!userData) return;

                const { data } = await adminFetchJson('/clients/getBranchNamesForUser', {
                    method: 'POST',
                    body: {},
                });
                if (data.success && data.data) {
                    const branchIdToCheck = userData.role === 1 
                        ? userData.selectedBranch 
                        : userData._id;
                    
                    const selectedBranch = data.data.find(
                        (b) => userData.role === 1 
                            ? b.masterId === branchIdToCheck 
                            : b._id === branchIdToCheck
                    );
                    
                    if (selectedBranch) {
                        const marketplaceEnabled = await isBranchMarketplaceEnabled(selectedBranch._id);

                        const enabled = {
                            online: selectedBranch.enableOnlineStore === true,
                            offline: selectedBranch.enableOfflineStore === true,
                            marketplace: marketplaceEnabled,
                        };
                        setEnabledStoreTypes(enabled);
                        
                        // Set default store type if not editing and storeType is empty
                        if (!_id && !couponData.storeType) {
                            if (enabled.online) setCouponData(prev => ({ ...prev, storeType: "online" }));
                            else if (enabled.offline) setCouponData(prev => ({ ...prev, storeType: "offline" }));
                            else if (enabled.marketplace) setCouponData(prev => ({ ...prev, storeType: "marketplace" }));
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching branch data:", error);
            }
        };

        fetchBranchData();
        // Mount-only: load enabled store types once for this session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (_id) {
            productsInitialized.current = false; // Reset ref when loading a different coupon
            getCouponsMethod({ _id: _id });
        }
    }, [_id]);

    useEffect(() => {
        const fetchProductNames = async () => {
            try {
                const data = await getOnlyProductNames();
                console.log("Product names data:", data);

                if (data.success) setProductOptions(data.data);
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };
        fetchProductNames();
    }, []);

    // Fetch categories with subcategories
    useEffect(() => {
        const fetchCategoriesData = async () => {
            try {
                const data = await getCategories({ includeSubcategories: true });
                if (data.success && data.data) {
                    const parentCategories = data.data.map((cat) => ({
                        value: cat._id,
                        label: cat.categoryName,
                        subcategories: cat.subcategories || []
                    }));
                    setCategoryOptions(parentCategories);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        };
        fetchCategoriesData();
    }, []);

    const allSubcategoryOptions = categoryOptions.flatMap((cat) =>
        (cat.subcategories || []).map((sub) => ({
            value: sub._id,
            label: `${cat.label} > ${sub.categoryName}`,
        }))
    );

    const normalizeId = (id) => {
        if (!id) return '';
        if (typeof id === 'object') return String(id.$oid || id);
        return String(id);
    };

    // Update subcategory options when category changes
    useEffect(() => {
        if (selectedCategory) {
            const category = categoryOptions.find(cat => cat.value === selectedCategory?.value);
            if (category && category.subcategories && category.subcategories.length > 0) {
                const subcats = category.subcategories.map(sub => ({
                    value: sub._id,
                    label: sub.categoryName
                }));
                setSubcategoryOptions(subcats);
            } else {
                setSubcategoryOptions([]);
            }
        } else {
            setSubcategoryOptions([]);
        }
    }, [selectedCategory, categoryOptions]);

    // Update couponData with products when editing
    useEffect(() => {
        if (_id && couponData.productIds && couponData.productIds.length > 0 && 
            productOptions.length > 0 && !productsInitialized.current) {
            const products = productOptions.filter(opt =>
                couponData.productIds.includes(opt.value)
            );
            if (products.length > 0) {
                setCouponData(prev => ({ ...prev, products }));
                productsInitialized.current = true;
            }
        }
        // Restore selection mode and category/subcategory when editing
        if (_id && couponData.selectionMode && categoryOptions.length > 0 && !productsInitialized.current) {
            const savedMode = couponData.selectionMode || 'manual';
            setSelectionMode(selectionModeOptions.find(m => m.value === savedMode) || selectionModeOptions[0]);
            
            if (savedMode === 'category' && couponData.categoryId) {
                const catIdStr = normalizeId(couponData.categoryId);
                const cat = categoryOptions.find(c => String(c.value) === catIdStr);
                setSelectedCategory(cat || null);
            }

            if (savedMode === 'subcategory' && couponData.categoryId) {
                const catIdStr = normalizeId(couponData.categoryId);
                const cat = categoryOptions.find(c => String(c.value) === catIdStr);
                setSelectedCategory(cat || null);

                if (couponData.subcategoryId && cat) {
                    const subIdStr = normalizeId(couponData.subcategoryId);
                    const parentCat = categoryOptions.find(c => String(c.value) === String(cat.value));
                    if (parentCat && parentCat.subcategories) {
                        const sub = parentCat.subcategories.find(s => String(s._id) === subIdStr);
                        if (sub) {
                            setSelectedSubcategory({ value: sub._id, label: sub.categoryName });
                        }
                    }
                }
            }

            if (savedMode === 'categories' && couponData.categoryIds?.length) {
                const savedCategoryIds = couponData.categoryIds.map(normalizeId);
                const cats = categoryOptions.filter(c => savedCategoryIds.includes(String(c.value)));
                setSelectedCategories(cats);
            }

            if (savedMode === 'subcategories' && couponData.subcategoryIds?.length) {
                const savedSubcategoryIds = couponData.subcategoryIds.map(normalizeId);
                const subcats = allSubcategoryOptions.filter(s => savedSubcategoryIds.includes(String(s.value)));
                setSelectedSubcategories(subcats);
            }

            if (savedMode !== 'manual') {
                productsInitialized.current = true;
            }
        }
    }, [productOptions, _id, couponData.productIds, couponData.selectionMode, couponData.categoryId, couponData.subcategoryId, couponData.categoryIds, couponData.subcategoryIds, categoryOptions, allSubcategoryOptions]);

    const getCouponsMethod = async (params) => {
        try {
            const data = await getCoupons(params);
            if (data.success && data.data && data.data.length) {
                const coupon = data.data[0];
                setCouponData({
                    ...coupon,
                    products: []  // Initialize products as an empty array
                });
            }
        } catch (error) {
            console.error("Error fetching coupon data:", error);
        }
    };

    // General field validation for input fields
    const validateField = (name, value) => {
        if (value.trim() === "") {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: "This field is required" }));
        } else {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Validate each input field
        validateField(name, value);

        setCouponData((prevData) => ({
            ...prevData,
            [name]: name === "couponCode" ? value.toUpperCase() : value,
        }));
    };

    // Update select field and validate if a value is selected
    const handleSelectChange = (selectedOption, action) => {
        const value = selectedOption?.value || "";
        if (value === "") {
            setErrors((prevErrors) => ({ ...prevErrors, [action.name]: "This field is required" }));
        } else {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[action.name];
                return newErrors;
            });
        }
        setCouponData((prevData) => ({
            ...prevData,
            [action.name]: value,
        }));
    };

    // For date fields, you can add custom validations if required
    const handleDateChange = (date, field) => {
        setCouponData((prevData) => ({
            ...prevData,
            [field]: date,
        }));
    };

    const calculateValidDays = () => {
        if (couponData.startDate && couponData.endDate) {
            const startDate = new Date(couponData.startDate);
            const endDate = new Date(couponData.endDate);
            const diffInTime = endDate - startDate;
            const days = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));
            return days > 0 ? days : 0;
        }
        return 0;
    };

    const generateAIDescription = async () => {
        if (!couponData.name || !couponData.discountValue) {
            toast.error("Coupon name and discount value are required for AI generation");
            return;
        }

        setIsGenerating(true);
        try {
            const response = await generateCouponDescription({
                name: couponData.name,
                discountType: couponData.discountType,
                discountValue: couponData.discountValue,
                minOrderValue: couponData.minOrderValue
            });

            if (response.success) {
                setCouponData(prev => ({
                    ...prev,
                    description: response.data.description
                }));
            }
        } catch (error) {
            console.error("AI Generation Error:", error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleProductsChange = (selected) => {
        setCouponData(prev => ({ ...prev, products: selected }));
        setErrors(prev => ({ ...prev, products: "" }));
    };

    const handleSelectAll = (e) => {
        const newProducts = e.target.checked ? productOptions : [];
        setCouponData(prev => ({ ...prev, products: newProducts }));
        setErrors(prev => ({ ...prev, products: "" }));
    };

    const handleSelectionModeChange = (option) => {
        setSelectionMode(option);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedCategories([]);
        setSelectedSubcategories([]);
        setCouponData(prev => ({ ...prev, products: [] }));
        setErrors(prev => ({ ...prev, products: "" }));
    };

    const handleCategoryChange = (option) => {
        setSelectedCategory(option);
        setSelectedSubcategory(null);
        setErrors(prev => ({ ...prev, products: "" }));
    };

    const handleSubcategoryChange = (option) => {
        setSelectedSubcategory(option);
        setErrors(prev => ({ ...prev, products: "" }));
    };

    const handleCategoriesChange = (options) => {
        setSelectedCategories(options || []);
        setErrors(prev => ({ ...prev, products: "" }));
    };

    const handleSubcategoriesChange = (options) => {
        setSelectedSubcategories(options || []);
        setErrors(prev => ({ ...prev, products: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const mode = selectionMode?.value || 'manual';
            
            const payload = {
                name: couponData.name,
                couponCode: couponData.couponCode,
                description: couponData.description,
                discountType: couponData.discountType,
                discountValue: couponData.discountValue,
                minOrderValue: couponData.minOrderValue,
                maxDiscountValue: couponData.maxDiscountValue,
                maxUsageTotal: couponData.maxUsageTotal,
                startDate: couponData.startDate,
                endDate: couponData.endDate,
                selectionMode: mode,
                productIds: mode === 'manual' ? couponData.products.map((p) => p.value) : [],
                categoryId: mode === 'category' || mode === 'subcategory'
                    ? (selectedCategory ? selectedCategory.value : null)
                    : null,
                subcategoryId: mode === 'subcategory'
                    ? (selectedSubcategory ? selectedSubcategory.value : null)
                    : null,
                categoryIds: mode === 'categories'
                    ? selectedCategories.map((c) => c.value)
                    : [],
                subcategoryIds: mode === 'subcategories'
                    ? selectedSubcategories.map((s) => s.value)
                    : [],
                storeType: couponData.storeType || "online",
                oneTimeUsePerUser: couponData.oneTimeUsePerUser || false,
                isHidden: couponData.isHidden || false
            };

            // For updates, add _id; for new coupons, generate a new id.
            if (_id) {
                payload._id = _id;
            } else {
                payload.id = `coupon-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            }

            // Call the API using your service methods:
            const data = _id
                ? await updateCoupon(payload)
                : await createCoupon(payload);

            if (data.success) {
                navigate("/coupons");
            } else {
                console.error("Failed to save coupon:", data.message);
            }
        } catch (error) {
            console.error("Error saving coupons:", error);
        }

        // Reset the form after submission
        setCouponData({
            name: "",
            couponCode: "",
            description: "",
            discountType: "percentage",
            discountValue: "",
            minOrderValue: "",
            maxDiscountValue: "",
            maxUsageTotal: "",
            startDate: new Date(),
            endDate: new Date(),
            products: [],  // Keep it reset as an empty array
            storeType: "online",
            oneTimeUsePerUser: false,
            isHidden: false,
        });
        setSelectionMode(selectionModeOptions[0]);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedCategories([]);
        setSelectedSubcategories([]);
        setErrors({});
    };

    return (
        <div className="bg-gradient-to-r from-indigo-200 to-blue-100">
            <NavigateBack targetBack className="pl-3" />
            <div className="bg-white p-6 rounded-tl-2xl rounded-tr-2xl">
                <h1 className="text-3xl font-bold text-gray-800 mb-6">Create Coupon</h1>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Coupon Name */}
                        <div>
                            <label className="labelStyles">Coupon Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={couponData.name}
                                onChange={handleChange}
                                autoComplete="off"
                                placeholder="Enter coupon name"
                                className={`customInput px-3 py-[9px] ${errors.name ? 'inputError' : 'inputNormal'}`}
                                required
                            />
                        </div>

                        {/* Coupon Code */}
                        <div>
                            <label className="labelStyles">Coupon Code *</label>
                            <input
                                type="text"
                                name="couponCode"
                                value={couponData.couponCode}
                                onChange={handleChange}
                                autoComplete="off"
                                placeholder="Enter coupon code"
                                className={`customInput px-3 py-[9px] ${errors.couponCode ? 'inputError' : 'inputNormal'}`}
                                required
                            />
                        </div>

                        {/* Discount Type */}
                        <div>
                            <label className="labelStyles">Discount Type *</label>
                            <Select
                                name="discountType"
                                options={discountTypeOptions}
                                value={discountTypeOptions.find(
                                    (option) => option.value === couponData.discountType
                                )}
                                onChange={handleSelectChange}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={customSelect}
                            />
                        </div>

                        {/* Store Type */}
                        <div>
                            <label className="labelStyles">Store Type *</label>
                            <Select
                                name="storeType"
                                options={storeTypeOptions}
                                value={storeTypeOptions.find(
                                    (option) => option.value === couponData.storeType
                                )}
                                onChange={handleSelectChange}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={customSelect}
                            />
                        </div>

                        {/* Discount Value */}
                        <div>
                            <label className="labelStyles">
                                Discount Value *
                                {couponData.discountType === "percentage"
                                    ? " (in %)"
                                    : " (in rupees)"}
                            </label>
                            <input
                                type="number"
                                name="discountValue"
                                value={couponData.discountValue}
                                onChange={handleChange}
                                placeholder="Enter discount value"
                                className={`customInput px-3 py-[9px] ${errors.discountValue ? 'inputError' : 'inputNormal'}`}
                                required
                            />
                        </div>

                        {/* Maximum Discount Value */}
                        <div>
                            <label className="labelStyles">
                                Maximum Discount Value per User * (in rupees)
                            </label>
                            <input
                                type="number"
                                name="maxDiscountValue"
                                value={couponData.maxDiscountValue}
                                onChange={handleChange}
                                placeholder="Enter max discount value"
                                className={`customInput px-3 py-[9px] ${errors.maxDiscountValue ? 'inputError' : 'inputNormal'}`}
                                required
                            />
                        </div>

                        {/* Minimum Order Value */}
                        <div>
                            <label className="labelStyles">
                                Minimum Order Value * (in rupees)
                            </label>
                            <input
                                type="number"
                                name="minOrderValue"
                                value={couponData.minOrderValue}
                                onChange={handleChange}
                                placeholder="Enter minimum order amount"
                                className={`customInput px-3 py-[9px] ${errors.minOrderValue ? 'inputError' : 'inputNormal'}`}
                                required
                            />
                            <p style={{ color: 'blue', fontSize: '14px', marginTop: '5px' }}>
                                Minimum Cart Value to apply this coupon: ₹{couponData.minOrderValue || 0}
                            </p>
                        </div>

                        {/* Maximum Total Usage */}
                        <div>
                            <label className="labelStyles">Maximum Total Usage *
                            </label>
                            <input
                                type="number"
                                name="maxUsageTotal"
                                value={couponData.maxUsageTotal}
                                onChange={handleChange}
                                placeholder="Enter overall usage limit"
                                className={`customInput px-3 py-[9px] ${errors.maxUsageTotal ? 'inputError' : 'inputNormal'}`}
                                required
                            />
                            {_id && couponData.currentUsage !== undefined && (
                                <p style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    Used Budget: ₹{couponData.currentUsage?.toFixed(2) || 0} / ₹{couponData.maxUsageTotal || 0}
                                </p>
                            )}
                        </div>

                        {/* One-Time Use Per Customer */}
                        <div className="sm:col-span-2">
                            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                                <input
                                    type="checkbox"
                                    id="oneTimeUsePerUser"
                                    name="oneTimeUsePerUser"
                                    checked={couponData.oneTimeUsePerUser}
                                    onChange={(e) => setCouponData(prev => ({ ...prev, oneTimeUsePerUser: e.target.checked }))}
                                    className="h-5 w-5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                                />
                                <div className="flex-1">
                                    <label
                                        htmlFor="oneTimeUsePerUser"
                                        className="text-base font-semibold text-gray-800 cursor-pointer"
                                    >
                                        One-Time Use Per Customer
                                    </label>
                                    <p className="text-sm text-gray-600 mt-1">
                                        When enabled, each customer can use this coupon only once. Even if the customer has eligible products and meets the minimum cart value, they won't be able to apply this coupon again after their first use.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Hide Coupon from Customers */}
                        <div className="sm:col-span-2">
                            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                                <input
                                    type="checkbox"
                                    id="isHidden"
                                    name="isHidden"
                                    checked={couponData.isHidden}
                                    onChange={(e) => setCouponData(prev => ({ ...prev, isHidden: e.target.checked }))}
                                    className="h-5 w-5 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                                />
                                <div className="flex-1">
                                    <label
                                        htmlFor="isHidden"
                                        className="text-base font-semibold text-gray-800 cursor-pointer"
                                    >
                                        Hide this coupon from all customers
                                    </label>
                                    <p className="text-sm text-gray-600 mt-1">
                                        When enabled, this coupon will not be visible in the coupon list. Customers can still apply it manually by entering the coupon code. Perfect for exclusive offers shared privately with select customers.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="sm:col-span-2">
                            <label className="block text-lg font-semibold text-gray-800 mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={couponData.description}
                                onChange={handleChange}
                                placeholder="Enter a brief description"
                                rows="4"
                                className={`customInput scrollbar-thin h-[132px] ${errors.description ? 'inputError' : 'inputNormal'}`}
                            />
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={generateAIDescription}
                                    disabled={isGenerating}
                                    className="absolute right-2 bottom-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 py-1.5 rounded-full shadow-lg hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-300"
                                >
                                    {isGenerating ? (
                                        <>
                                            <span className="animate-spin">🌀</span>
                                            <span className="bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-white">
                                                Generating...
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-sm transition-transform group-hover:scale-125 group-hover:-rotate-12">
                                                🤖
                                            </span>
                                            <span className="text-sm bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-white">
                                                AI Generate
                                            </span>
                                        </>
                                    )}
                                </button>

                                <div className="absolute -bottom-[10px] right-1 text-xs text-gray-400">
                                    Powered by ChatGPT-4
                                </div>
                            </div>
                            {/* Preview for description */}
                            {/* <div
                                className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                                style={{ whiteSpace: "pre-wrap" }}
                            >
                                {couponData.description}
                            </div> */}
                        </div>

                        {/* Products selection dropdown  */}
                        <div className="sm:col-span-2">
                            <label className="labelStyles">Select Products</label>

                            {/* Selection Mode Toggle */}
                            <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-600 mb-1">How do you want to add products?</label>
                                <Select
                                    options={selectionModeOptions}
                                    value={selectionMode}
                                    onChange={handleSelectionModeChange}
                                    styles={customSelect}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isSearchable={false}
                                />
                            </div>

                            {/* Manual Product Selection */}
                            {selectionMode?.value === 'manual' && (
                                <>
                                    <div className="mb-2 flex items-center">
                                        <input
                                            type="checkbox"
                                            id="selectAllProducts"
                                            checked={couponData.products.length === (productOptions?.length || 0)}
                                            onChange={handleSelectAll}
                                            className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                                        />
                                        <label
                                            htmlFor="selectAllProducts"
                                            className="ml-2 text-sm text-gray-700"
                                        >
                                            Select all products
                                        </label>
                                    </div>

                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        menuPlacement="top"
                                        options={productOptions}
                                        value={couponData.products}
                                        onChange={handleProductsChange}
                                        styles={{
                                            ...customSelect,
                                            valueContainer: (provided) => ({
                                                ...provided,
                                                maxHeight: "100px",
                                                overflowY: "auto"
                                            })
                                        }}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Search products..."
                                    />
                                </>
                            )}

                            {/* Category Selection */}
                            {selectionMode?.value === 'category' && (
                                <div>
                                    <Select
                                        options={categoryOptions}
                                        value={selectedCategory}
                                        onChange={handleCategoryChange}
                                        styles={customSelect}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isClearable
                                        placeholder="Select a category..."
                                        menuPlacement="top"
                                    />
                                    {selectedCategory && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            All products from "{selectedCategory.label}" will be linked to this coupon
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Multiple Categories Selection */}
                            {selectionMode?.value === 'categories' && (
                                <div>
                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        options={categoryOptions}
                                        value={selectedCategories}
                                        onChange={handleCategoriesChange}
                                        styles={{
                                            ...customSelect,
                                            valueContainer: (provided) => ({
                                                ...provided,
                                                maxHeight: "100px",
                                                overflowY: "auto"
                                            })
                                        }}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Select categories..."
                                        menuPlacement="top"
                                    />
                                    {selectedCategories.length > 0 && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            All products from {selectedCategories.length} selected categor{selectedCategories.length === 1 ? 'y' : 'ies'} will be linked to this coupon
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Subcategory Selection */}
                            {selectionMode?.value === 'subcategory' && (
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Select Parent Category</label>
                                        <Select
                                            options={categoryOptions}
                                            value={selectedCategory}
                                            onChange={handleCategoryChange}
                                            styles={customSelect}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            isClearable
                                            placeholder="First, select a parent category..."
                                            menuPlacement="top"
                                        />
                                    </div>
                                    {selectedCategory && subcategoryOptions.length > 0 && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Select Subcategory</label>
                                            <Select
                                                options={subcategoryOptions}
                                                value={selectedSubcategory}
                                                onChange={handleSubcategoryChange}
                                                styles={customSelect}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                isClearable
                                                placeholder="Select a subcategory..."
                                                menuPlacement="top"
                                            />
                                            {selectedSubcategory && (
                                                <p className="mt-1 text-xs text-gray-500">
                                                    All products from "{selectedSubcategory.label}" will be linked to this coupon
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {selectedCategory && subcategoryOptions.length === 0 && (
                                        <p className="text-sm text-amber-600">
                                            This category has no subcategories. Please select a different category or use the Category selection mode.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Multiple Subcategories Selection */}
                            {selectionMode?.value === 'subcategories' && (
                                <div>
                                    {allSubcategoryOptions.length > 0 ? (
                                        <>
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                options={allSubcategoryOptions}
                                                value={selectedSubcategories}
                                                onChange={handleSubcategoriesChange}
                                                styles={{
                                                    ...customSelect,
                                                    valueContainer: (provided) => ({
                                                        ...provided,
                                                        maxHeight: "100px",
                                                        overflowY: "auto"
                                                    })
                                                }}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                placeholder="Select sub categories..."
                                                menuPlacement="top"
                                            />
                                            {selectedSubcategories.length > 0 && (
                                                <p className="mt-1 text-xs text-gray-500">
                                                    All products from {selectedSubcategories.length} selected sub categor{selectedSubcategories.length === 1 ? 'y' : 'ies'} will be linked to this coupon
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="text-sm text-amber-600">
                                            No sub categories available. Please create sub categories first or use another selection mode.
                                        </p>
                                    )}
                                </div>
                            )}

                        </div>

                        {/* Start Date */}
                        <div>
                            <label className="labelStyles">Start Date *</label>
                            <DatePicker
                                selected={couponData.startDate}
                                onChange={(date) => handleDateChange(date, "startDate")}
                                dateFormat="dd/MM/yyyy"
                                minDate={new Date()} // Disable past dates
                                className={`customInput px-3 py-[9px] ${errors.startDate ? 'inputError' : 'inputNormal'}`}
                                popperPlacement="bottom-start"
                                calendarClassName="custom-datepicker"
                            />
                        </div>

                        {/* Expiration Date */}
                        <div>
                            <label className="labelStyles">Expiration Date *</label>
                            <DatePicker
                                selected={couponData.endDate}
                                onChange={(date) => handleDateChange(date, "endDate")}
                                dateFormat="dd/MM/yyyy"
                                minDate={couponData.startDate || new Date()} // Disable dates before start date
                                className={`customInput px-3 py-[9px] ${errors.endDate ? 'inputError' : 'inputNormal'}`}
                                popperPlacement="bottom-start"
                                calendarClassName="custom-datepicker"
                            />

                            {/* Coupon Status */}
                            {couponData.startDate && couponData.endDate && (
                                <div className="mt-1">
                                    {new Date(couponData.endDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) ? (
                                        <span className="text-red-600 font-bold">Coupon expired</span>
                                    ) : (
                                        <span className="text-gray-600">
                                            Coupon valid <span role="img" aria-label="Coupon">🎫</span>{" "}
                                            <span className="font-bold">{calculateValidDays()} days</span>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between mt-6 gap-4">
                        <button
                            onClick={() => navigate('/coupons')}
                            className="px-3 py-2 text-lg font-semibold text-gray-700 bg-gray-300 rounded-lg hover:bg-gray-400 transition w-full"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-3 py-2 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition w-full"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateCoupon;
